/**
 * Variant siblings (PDP)
 *
 * Behavior:
 * - Canonical PDP is the "master" handle for a group
 * - When arriving with ?base=...&sibling=handle, we keep URL as master and AJAX-swap product content
 * - Color picker lists all sibling products and swaps between them without navigating
 * - When swapping, attempts to keep other options (e.g. size) matched by option name + value
 */

(() => {
  // Prevent duplicate initialization: this script may be reinjected during product-info HTML swaps.
  if (window.__VS_PDP_INIT__) return;
  window.__VS_PDP_INIT__ = true;

  const normalize = (s) => (s || '').replace(/\s+/g, ' ').trim();
  const normLower = (s) => normalize(s).toLowerCase();
  const CONNECTOR_WORDS = new Set([
    'edizione',
    'limitata',
    'charm',
    'in',
    'limited',
    'edition',
    // materials/model words that should never start the "color" suffix
    'pelle',
    'liscia',
    'liscio',
    // model/material words that should never be treated as "color"
    'suede',
    'monogrammata',
    'monogrammato',
    'monogramm',
  ]);

  const QS = new URLSearchParams(window.location.search);

  function getStorefrontRoot() {
    const root = window.routes?.root_url || window.Shopify?.routes?.root || '/';
    return root.endsWith('/') ? root : `${root}/`;
  }

  function canonicalHandle(handle) {
    return (handle || '').replace(/-en$/, '');
  }

  function imageKey(url) {
    if (!url) return '';
    return url.split('?')[0].replace(/^\/\//, '');
  }

  function deriveHandlePrefix(handle) {
    const canon = canonicalHandle(handle);
    const parts = canon.split('-');
    const inIdx = parts.indexOf('in');
    if (inIdx >= 0) return `${parts.slice(0, inIdx + 1).join('-')}-`;
    const lastDash = canon.lastIndexOf('-');
    return lastDash > 0 ? `${canon.slice(0, lastDash + 1)}` : `${canon}-`;
  }

  function localeCacheKey(query, root) {
    return `${root}|${normalize(query)}`;
  }

  function withItHandle(siblings) {
    return (siblings || []).map((s) => ({ ...s, itHandle: s.itHandle || s.handle }));
  }

  function resolveToItHandle(rawHandle, itSiblings, localeSiblings) {
    if (!rawHandle) return null;
    const canon = canonicalHandle(rawHandle);
    const itDirect = itSiblings.find(
      (s) => s.handle === rawHandle || canonicalHandle(s.handle) === canon
    );
    if (itDirect) return itDirect.handle;

    const localeMatch = localeSiblings?.find(
      (s) =>
        s.handle === rawHandle ||
        canonicalHandle(s.handle) === canon ||
        s.itHandle === rawHandle ||
        canonicalHandle(s.itHandle || '') === canon
    );
    if (localeMatch?.itHandle) return localeMatch.itHandle;
    return rawHandle;
  }

  function resolveToLocaleHandle(itHandle, localeSiblings) {
    if (!itHandle || !localeSiblings?.length) return itHandle;
    const canon = canonicalHandle(itHandle);
    const match = localeSiblings.find(
      (s) =>
        s.itHandle === itHandle ||
        s.handle === itHandle ||
        canonicalHandle(s.handle) === canon ||
        canonicalHandle(s.itHandle || '') === canon
    );
    return match?.handle || null;
  }

  function resolveSiblingHandle(rawHandle, siblings) {
    if (!rawHandle || !siblings?.length) return rawHandle;
    const resolved = resolveToLocaleHandle(
      resolveToItHandle(rawHandle, siblings, siblings),
      siblings
    );
    return resolved || rawHandle;
  }

  function buildPdpUrl(masterHandle, base, siblingHandle, root) {
    const url = new URL(`${root}products/${masterHandle}`, window.location.origin);
    if (base) url.searchParams.set('base', base);
    if (siblingHandle) url.searchParams.set('sibling', siblingHandle);
    return `${url.pathname}${url.search}`;
  }

  function exposePdpHelpers() {
    window.__VS_PDP__ = {
      buildReturnToForLocale(localeCode) {
        const state = window.__VS_PDP_STATE__;
        if (!state?.itSiblings?.length || !state.base) return null;

        const isEn = localeCode === 'en' || `${localeCode}`.startsWith('en');
        const targetRoot = isEn ? '/en/' : '/';
        const targetSiblings = isEn ? state.enSiblings : state.itSiblings;
        if (!targetSiblings?.length) return null;

        const master = pickMasterFromItalian(state.itSiblings, targetSiblings);
        const activeIt =
          state.activeItHandle ||
          resolveToItHandle(state.activeHandle, state.itSiblings, state.siblings);
        const active = resolveToLocaleHandle(activeIt, targetSiblings) || activeIt;
        return buildPdpUrl(master, state.base, active, targetRoot);
      },

      mapProductPathForLocale(pathname, localeCode) {
        const handle = decodeURIComponent((pathname.match(/\/products\/([^\/?#]+)/) || [])[1] || '');
        if (!handle) return pathname;
        const isEn = localeCode === 'en' || `${localeCode}`.startsWith('en');
        const targetRoot = isEn ? '/en/' : '/';
        const state = window.__VS_PDP_STATE__;
        if (state?.itSiblings?.length) {
          return window.__VS_PDP__.buildReturnToForLocale(localeCode);
        }
        const canon = canonicalHandle(handle);
        const targetHandle = isEn
          ? handle.endsWith('-en')
            ? handle
            : `${canon}-en`
          : canon;
        return `${targetRoot}products/${targetHandle}`;
      },
    };
  }

  function filterSiblingsForLocale(siblings, root) {
    const storefrontRoot = root || getStorefrontRoot();
    if (storefrontRoot === '/') {
      return siblings.filter((s) => {
        const path = (s.url || '').split('?')[0];
        return path.startsWith('/products/') && !path.startsWith('/en/');
      });
    }

    const filtered = siblings.filter((s) => {
      const path = (s.url || '').split('?')[0];
      return path.startsWith(`${storefrontRoot}products/`);
    });

    return filtered.length >= 2 ? filtered : siblings;
  }

  const suggestCache = new Map();
  const STATE = (window.__VS_PDP_STATE__ = window.__VS_PDP_STATE__ || {
    base: null,
    siblings: null,
    itSiblings: null,
    enSiblings: null,
    masterHandle: null,
    activeHandle: null,
    activeItHandle: null,
    pendingSelections: null,
    pendingInitialSwap: false,
  });

  function releasePdpPending(productInfo) {
    window.__VS_PENDING_SWAP__ = false;
    document.documentElement.classList.remove('vs-pdp-pending');
    if (productInfo) productInfo.classList.remove('vs-swap-loading');
  }

  function markPdpPending(productInfo) {
    STATE.pendingInitialSwap = true;
    window.__VS_PENDING_SWAP__ = true;
    document.documentElement.classList.add('vs-pdp-pending');
    setSwapLoading(productInfo, true);
  }

  function finalizeSiblingUi(productInfo) {
    if (!productInfo || !STATE.base || !STATE.siblings) return;

    if (STATE.masterHandle && getPathHandle() === STATE.masterHandle) {
      setPdpUrlFrozen(productInfo);
    }

    installNativeColorCleaner(productInfo);
    simplifyNativeColorOption(productInfo);

    if (STATE.activeHandle) {
      renderPicker(productInfo, STATE.base, STATE.siblings, STATE.activeHandle);
    }
  }

  async function fetchSearchAtRoot(query, root) {
    const q = normalize(query);
    if (!q) return [];
    const cacheKey = localeCacheKey(q, root);
    if (suggestCache.has(cacheKey)) return await suggestCache.get(cacheKey);

    const p = (async () => {
      const toItems = (raw) =>
        (raw || [])
          .map((r) => ({
            title: normalize(r.title),
            titleLower: normLower(r.title),
            url: r.url,
            image: r.image || r.featured_image || null,
            handle:
              r.handle ||
              (r.url ? r.url.split('?')[0].split('/').filter(Boolean).pop() : null),
          }))
          .filter((x) => x.handle && x.url && x.title);

      const fetchPage = async (page) => {
        const url =
          `${root}search?view=vs-siblings` +
          `&type=product` +
          `&options[prefix]=last` +
          `&options[unavailable_products]=show` +
          `&page=${page}` +
          `&q=${encodeURIComponent(q)}`;
        const res = await fetch(url, { credentials: 'same-origin' });
        if (!res.ok) return { pages: 1, items: [] };
        const txt = await res.text();
        let data = null;
        try {
          data = JSON.parse(txt);
        } catch {
          return { pages: 1, items: [] };
        }
        return { pages: Number(data?.pages || 1) || 1, items: toItems(data?.products) };
      };

      const first = await fetchPage(1);
      const map = new Map(first.items.map((it) => [it.handle, it]));

      const totalPages = Math.max(1, first.pages);
      const cap = Math.min(totalPages, 20);
      for (let page = 2; page <= cap; page++) {
        const next = await fetchPage(page);
        next.items.forEach((it) => map.set(it.handle, it));
      }

      return Array.from(map.values());
    })();

    suggestCache.set(cacheKey, p);
    return await p;
  }

  // Grouping always uses the Italian catalog (stable titles/handles).
  async function fetchSearchProducts(query) {
    return fetchSearchAtRoot(query, '/');
  }

  function isSameHandleFamily(handle, prefix, itSiblings) {
    const canon = canonicalHandle(handle);
    if (prefix && canon.startsWith(prefix)) return true;
    return (itSiblings || []).some((it) => canonicalHandle(it.handle) === canon);
  }

  async function localizeSiblingsFromItalian(itSiblings, base, targetRoot) {
    const root = targetRoot || getStorefrontRoot();
    const taggedIt = withItHandle(itSiblings);
    if (root === '/') return taggedIt;

    const localeProducts = await fetchSearchAtRoot(base, root);
    const prefix = deriveHandlePrefix(taggedIt[0]?.handle);
    const familyProducts = localeProducts.filter((p) =>
      isSameHandleFamily(p.handle, prefix, taggedIt)
    );

    const usedHandles = new Set();
    const mapped = [];

    for (const it of taggedIt) {
      const canon = canonicalHandle(it.handle);
      const imgKey = imageKey(it.image);
      let match = null;

      // 1. Handle match within the same product family (amazzonia-mini-in-*)
      match = familyProducts.find(
        (p) => !usedHandles.has(p.handle) && canonicalHandle(p.handle) === canon
      );

      // 2. Handle match anywhere in locale search results
      if (!match) {
        match = localeProducts.find(
          (p) => !usedHandles.has(p.handle) && canonicalHandle(p.handle) === canon
        );
      }

      // 3. Image match within family
      if (!match && imgKey) {
        match = familyProducts.find(
          (p) => !usedHandles.has(p.handle) && imageKey(p.image) === imgKey
        );
      }

      // 4. Image match across all locale hits (covers mislabeled EN products, e.g. Toffee → softy-fabric-2)
      if (!match && imgKey) {
        match = localeProducts.find(
          (p) => !usedHandles.has(p.handle) && imageKey(p.image) === imgKey
        );
      }

      if (match) {
        usedHandles.add(match.handle);
        mapped.push({ ...match, itHandle: it.handle });
      }
    }

    let result = filterSiblingsForLocale(mapped, root);
    return result.length >= 2 ? result : taggedIt;
  }

  async function buildSiblingGroup(base) {
    const itProducts = await fetchSearchAtRoot(base, '/');
    const itSiblings = siblingsForBase(itProducts, base);
    if (itSiblings.length < 2) return null;

    const currentRoot = getStorefrontRoot();
    const siblings =
      currentRoot === '/'
        ? withItHandle(itSiblings)
        : await localizeSiblingsFromItalian(itSiblings, base, currentRoot);
        const enSiblings = await localizeSiblingsFromItalian(itSiblings, base, '/en/');

    return {
      base,
      itSiblings: withItHandle(itSiblings),
      siblings,
      enSiblings: withItHandle(enSiblings),
      masterHandle: pickMasterFromItalian(itSiblings, siblings),
    };
  }

  function siblingsForBase(products, base) {
    const prefix = normLower(base) + ' ';
    if (prefix.trim().length < 2) return [];
    return products.filter((p) => {
      if (!p.titleLower.startsWith(prefix) || p.titleLower.length <= prefix.length) return false;
      return isValidBaseForTitle(p.title, base);
    });
  }

  function pickMasterHandle(siblings) {
    return siblings
      .map((s) => s.handle)
      .filter(Boolean)
      .sort((a, b) => a.localeCompare(b, 'en'))[0];
  }

  // Master is always the Italian group's first handle, mapped to the target locale.
  function pickMasterFromItalian(itSiblings, localeSiblings) {
    const itMaster = pickMasterHandle(withItHandle(itSiblings));
    return resolveToLocaleHandle(itMaster, localeSiblings) || itMaster;
  }

  function candidatesFromTitle(title) {
    const words = normalize(title).split(' ').filter(Boolean);
    if (words.length < 2) return [];
    const out = [];
    for (let i = 1; i <= words.length - 1; i++) out.push(words.slice(0, i).join(' '));
    return out;
  }

  function suffixInfoFor(title, baseWordCount) {
    const words = normalize(title).split(' ').filter(Boolean);
    const suffixWords = words.slice(baseWordCount);
    const first = (suffixWords[0] || '').toLowerCase();
    return { suffixWords, suffixLen: suffixWords.length, firstSuffixWord: first };
  }

  function isValidBaseForTitle(title, base) {
    const baseWordCount = normalize(base).split(' ').filter(Boolean).length;
    const { suffixLen, firstSuffixWord } = suffixInfoFor(title, baseWordCount);
    if (suffixLen < 1 || suffixLen > 3) return false;
    if (!firstSuffixWord) return false;
    if (CONNECTOR_WORDS.has(firstSuffixWord)) return false;
    return true;
  }

  function groupContainsClickedProduct(siblings, title, handle, image) {
    if (!handle && !image) return true;

    const canon = canonicalHandle(handle || '');
    const imgKey = imageKey(image);
    const titleNorm = normLower(title);

    return siblings.some((s) => {
      if (imgKey && imageKey(s.image) === imgKey) return true;
      if (canon && canonicalHandle(s.handle) === canon) return true;
      if (canon && canonicalHandle(s.itHandle || '') === canon) return true;
      if (titleNorm && normLower(s.title) === titleNorm) return true;
      return false;
    });
  }

  function itSearchQueriesFromHandle(handle, localeTitle) {
    const canon = canonicalHandle(handle);
    const queries = [];

    if (canon.includes('piattina') && canon.includes('mini')) {
      queries.push('Piattina Mini in');
      queries.push('Mini Piattina in');
    }
    if (canon.includes('piattina') && (canon.includes('edizione') || canon.includes('limitata'))) {
      queries.push('Piattina Edizione Limitata in');
    } else if (canon.includes('piattina')) {
      queries.push('Piattina in');
    }
    if (canon.includes('amazzonia') && canon.includes('mini')) {
      queries.push('Mini Amazzonia in');
    } else if (canon.includes('amazzonia')) {
      queries.push('Amazzonia in');
    }

    const inMatch = normalize(localeTitle).match(/\bin\s+(.+)$/i);
    if (inMatch?.[1]) {
      const suffix = inMatch[1].trim();
      if (canon.includes('piattina') && canon.includes('mini')) {
        queries.push(`Piattina Mini in ${suffix}`);
      }
    }

    const lastSeg = canon.split('-').pop();
    if (lastSeg && lastSeg.length > 2) queries.push(lastSeg);

    return [...new Set(queries)];
  }

  async function resolveItalianTitleFromLocale(handle, localeTitle, image) {
    if (getStorefrontRoot() === '/') return normalize(localeTitle);

    const imgKey = imageKey(image);
    const canon = canonicalHandle(handle);
    const titleSuffix = normalize(localeTitle).match(/\bin\s+(.+)$/i)?.[1]?.trim();

    for (const q of itSearchQueriesFromHandle(handle, localeTitle)) {
      const products = await fetchSearchAtRoot(q, '/');
      if (!products.length) continue;

      if (imgKey) {
        const byImg = products.find((p) => imageKey(p.image) === imgKey);
        if (byImg) return byImg.title;
      }

      const byHandle = products.find((p) => canonicalHandle(p.handle) === canon);
      if (byHandle) return byHandle.title;

      if (titleSuffix && q.includes(' ')) {
        const suf = normLower(titleSuffix);
        const bySuffix = products.find((p) => {
          const tl = normLower(p.title);
          return tl.endsWith(` ${suf}`) || tl.endsWith(suf);
        });
        if (bySuffix) return bySuffix.title;
      }
    }

    return null;
  }

  async function inferGroupByTitlePrefix(fullTitle, handle, image) {
    const candidates = candidatesFromTitle(fullTitle);
    if (!candidates.length) return null;

    let best = null;
    for (const base of candidates) {
      if (!isValidBaseForTitle(fullTitle, base)) continue;
      const products = await fetchSearchProducts(base);
      const siblings = siblingsForBase(products, base);
      if (siblings.length < 2) continue;
      if (handle && !groupContainsClickedProduct(siblings, fullTitle, handle, image)) continue;

      const baseWordCount = normalize(base).split(' ').filter(Boolean).length;
      const suffixWordCounts = siblings.map((s) => suffixInfoFor(s.title, baseWordCount).suffixLen);
      const maxSuffixWords = Math.max(...suffixWordCounts);
      if (maxSuffixWords > 3) continue;

      const score = {
        count: siblings.length,
        baseWordCount,
        maxSuffixWords,
      };

      if (
        !best ||
        score.count > best.score.count ||
        (score.count === best.score.count && score.baseWordCount > best.score.baseWordCount) ||
        (score.count === best.score.count &&
          score.baseWordCount === best.score.baseWordCount &&
          score.maxSuffixWords < best.score.maxSuffixWords)
      ) {
        best = { base, siblings, score };
      }
    }

    if (!best) return null;
    const group = await buildSiblingGroup(best.base);
    if (!group) return null;
    return {
      base: group.base,
      siblings: group.siblings,
      itSiblings: group.itSiblings,
      enSiblings: group.enSiblings,
      masterHandle: group.masterHandle,
    };
  }

  async function inferGroupFromTitle(fullTitle, handle, image) {
    const direct = await inferGroupByTitlePrefix(fullTitle, handle, image);
    if (direct) return direct;

    if (!handle) return null;

    const itTitle = await resolveItalianTitleFromLocale(handle, fullTitle, image);
    if (!itTitle) return null;

    return await inferGroupByTitlePrefix(itTitle, handle, image);
  }

  function getProductImage(productInfo) {
    return (
      productInfo?.querySelector('.product__media img, .media img, [id^="Gallery"] img')?.src ||
      document.querySelector('meta[property="og:image"]')?.content ||
      null
    );
  }

  function getProductInfo() {
    const nodes = Array.from(document.querySelectorAll("product-info[id^='MainProduct-']"));
    if (!nodes.length) return null;
    const visible = nodes.find((node) => node.style.display !== 'none');
    return visible || nodes[nodes.length - 1];
  }

  function getPathHandle() {
    return decodeURIComponent((window.location.pathname.match(/\/products\/([^\/?#]+)/) || [])[1] || '');
  }

  function getCurrentHandle(productInfo) {
    return (
      productInfo?.dataset?.productHandle ||
      decodeURIComponent((window.location.pathname.match(/\/products\/([^\/?#]+)/) || [])[1] || '')
    );
  }

  function getCurrentTitle(productInfo) {
    return productInfo?.dataset?.productTitle || normalize(document.querySelector('h1')?.textContent);
  }

  function captureSelectedOptions(productInfo) {
    const selections = [];
    const vs = productInfo.querySelector('variant-selects');
    if (!vs) return selections;

    // selects
    vs.querySelectorAll('select[name^="options["]').forEach((sel) => {
      const m = sel.name.match(/^options\[(.*)\]$/);
      if (!m) return;
      selections.push({ name: m[1], value: sel.value });
    });

    // radios (fallback): store group name (input_name contains option name)
    vs.querySelectorAll('fieldset input[type="radio"]:checked').forEach((inp) => {
      const name = inp.name ? inp.name.split('-').slice(0, -1).join('-') : '';
      if (name) selections.push({ name, value: inp.value });
    });

    return selections;
  }

  function applySelectedOptions(productInfo, selections) {
    const vs = productInfo.querySelector('variant-selects');
    if (!vs || !selections?.length) return;

    selections.forEach(({ name, value }) => {
      // Prefer selects by exact option name
      const sel = vs.querySelector(`select[name="options[${CSS.escape(name)}]"]`);
      if (sel) {
        const opt = Array.from(sel.options).find((o) => o.value === value);
        if (opt) {
          sel.value = value;
          sel.dispatchEvent(new Event('change', { bubbles: true }));
          return;
        }
      }

      // Radios: match by value
      const radios = Array.from(vs.querySelectorAll('input[type="radio"]'));
      const radio = radios.find((r) => r.value === value);
      if (radio) {
        radio.checked = true;
        radio.dispatchEvent(new Event('change', { bubbles: true }));
      }
    });
  }

  function ensurePickerMount(productInfo) {
    let mount = productInfo.querySelector('[data-auto-color-siblings]');
    if (mount) return mount;

    mount = document.createElement('section');
    mount.className = 'product-siblings auto';
    mount.setAttribute('data-auto-color-siblings', 'true');

    // Place the swatches right above the add-to-cart area (closer, less whitespace).
    const buyForm = productInfo.querySelector('section.product-form');
    if (buyForm?.parentNode) {
      buyForm.parentNode.insertBefore(mount, buyForm);
      return mount;
    }

    // Fallback: after variant picker if present
    const variantPicker = productInfo.querySelector('section.variant-picker');
    if (variantPicker?.parentNode) {
      variantPicker.parentNode.insertBefore(mount, variantPicker.nextSibling);
      return mount;
    }

    // Last resort: inside options container
    const options = productInfo.querySelector('.etheryx-product-options .kontainer') || productInfo;
    options.prepend(mount);
    return mount;
  }

  function simplifyNativeColorOption(productInfo) {
    const root = productInfo?.querySelector('section.variant-picker') || productInfo;
    if (!root) return;

    const optionContainers = Array.from(root.querySelectorAll('variant-selects .product-form__input'));
    optionContainers.forEach((el) => {
      // Determine option name as robustly as possible:
      // - dropdown: select[name="options[Colore]"]
      // - swatch/button: inputs use name like "Colore-1" (option.name + '-' + option.position)
      // - fallback: legend/label text
      let optionName = '';

      const sel = el.querySelector('select[name^="options["]');
      if (sel?.name) {
        const m = sel.name.match(/^options\[(.*)\]$/);
        if (m?.[1]) optionName = normalize(m[1]);
      }

      if (!optionName) {
        const inp = el.querySelector('input[type="radio"][name], input[type="hidden"][name]');
        if (inp?.name) optionName = normalize(inp.name.split('-')[0]);
      }

      if (!optionName) {
        const legend = el.querySelector('legend.form__label');
        const label = el.querySelector('label.form__label');
        const raw = normalize((legend || label)?.textContent);
        optionName = normalize((raw || '').split(':')[0]);
      }

      const lowerName = normLower(optionName);
      if (lowerName !== 'colore' && lowerName !== 'color') return;

      // Keep the "COLORE:" row, but remove any duplicated UI (selected value, swatch, inputs),
      // since we render our own sibling color selector below.
      el.style.display = '';
      el.setAttribute('data-vs-simplified-color-option', 'true');

      // 1) Keep label/legend but remove dynamic selected value next to "COLORE:"
      const legend = el.querySelector('legend.form__label');
      if (legend) {
        const selectedValue = legend.querySelector('[data-selected-value]');
        if (selectedValue) selectedValue.style.display = 'none';
        // Ensure it ends with ":" visually (theme already prints it, but keep safe)
        const baseText = normalize(legend.textContent).split(':')[0];
        // Only adjust if legend doesn't already include ':'
        if (!legend.textContent.includes(':') && baseText) {
          legend.textContent = `${baseText}:`;
        }
      }

      const label = el.querySelector('label.form__label');
      if (label) {
        // Some layouts don't have ":"; add it for consistency with merchant expectation.
        if (!label.textContent.includes(':')) label.textContent = `${normalize(label.textContent)}:`;
      }

      // 2) Hide any native swatch circle shown above the value
      el.querySelectorAll('.dropdown-swatch, [data-selected-value].dropdown-swatch').forEach((n) => {
        n.style.display = 'none';
      });

      // 3) Hide the native color controls (swatch group or select), but keep in DOM for variant logic.
      const swatchGroup = el.querySelector('.swatch-group');
      if (swatchGroup) swatchGroup.style.display = 'none';

      const selectWrap = el.querySelector('.select');
      if (selectWrap) selectWrap.style.display = 'none';
      const selectEl = el.querySelector('select[name^="options["]');
      if (selectEl) selectEl.style.display = 'none';
    });
  }

  function installNativeColorCleaner(productInfo) {
    const vs = productInfo?.querySelector('variant-selects');
    if (!vs) return;
    if (vs.dataset.vsColorCleanerInstalled === 'true') return;
    vs.dataset.vsColorCleanerInstalled = 'true';

    let raf = 0;
    const schedule = () => {
      // Only when siblings mode is active
      if (!STATE.base || !STATE.siblings) return;
      if (raf) cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => simplifyNativeColorOption(productInfo));
      // Some theme updates happen async after change handlers; re-apply once more.
      setTimeout(() => simplifyNativeColorOption(productInfo), 50);
    };

    vs.addEventListener('change', schedule, true);

    const obs = new MutationObserver(() => schedule());
    obs.observe(vs, { childList: true, subtree: true });

    // initial
    schedule();
  }

  function ensurePickerStyles() {
    if (document.getElementById('vs-siblings-style')) return;
    const style = document.createElement('style');
    style.id = 'vs-siblings-style';
    style.textContent = `
      [data-auto-color-siblings] { margin: 0; padding: 0; }
      /* Horizontal chips (wrap) */
      [data-auto-color-siblings] .vs-heading {
        font-size: 12px;
        letter-spacing: .08em;
        text-transform: uppercase;
        margin: 0 0 8px 0;
        color: var(--labelTitleColor, var(--colorFaded));
      }
      [data-auto-color-siblings] .vs-grid { display:flex; flex-wrap:wrap; gap: 8px; margin: 0; padding: 0; }
      [data-auto-color-siblings] .vs-chip {
        display:flex;
        align-items:center;
        gap: 8px;
        padding: 6px 10px;
        border: 1px solid rgba(0,0,0,.18);
        border-radius: 999px;
        background: #fff;
        cursor: pointer;
        max-width: 100%;
      }
      [data-auto-color-siblings] .vs-chip:hover { background: rgba(0,0,0,.03); }
      [data-auto-color-siblings] .vs-chip:focus-visible { outline: 2px solid #000; outline-offset: 2px; }
      [data-auto-color-siblings] .vs-chip[aria-current="true"] {
        border-color: rgba(0,0,0,.6);
        box-shadow: 0 0 0 1px rgba(0,0,0,.2) inset;
      }
      [data-auto-color-siblings] .vs-thumb {
        width: 22px; height: 22px;
        border-radius: 7px;
        overflow: hidden;
        border: 1px solid rgba(0,0,0,.15);
        flex: 0 0 auto;
      }
      [data-auto-color-siblings] .vs-thumb img { width:100%; height:100%; object-fit:cover; display:block; }
      [data-auto-color-siblings] .vs-chip-name { font-size: 13px; line-height: 1.05; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
      product-info.vs-swap-loading { pointer-events: none; }
    `;
    document.head.appendChild(style);
  }

  function cleanProductUrl(url) {
    return (url || '').split('?')[0];
  }

  function setSwapLoading(productInfo, loading) {
    if (!productInfo) return;
    productInfo.classList.toggle('vs-swap-loading', loading);
  }

  function parseSectionHtml(responseText) {
    return new DOMParser().parseFromString(responseText, 'text/html');
  }

  function colorFromTitle(title, base) {
    const baseWordCount = normalize(base).split(' ').filter(Boolean).length;
    return normalize(title).split(' ').filter(Boolean).slice(baseWordCount).join(' ').trim();
  }

  function renderPicker(productInfo, base, siblings, activeHandle) {
    const mount = ensurePickerMount(productInfo);
    ensurePickerStyles();
    const colorLabel = window.vsSiblingsStrings?.colorLabel || 'Color';
    const itByHandle = new Map(
      (STATE.itSiblings || []).map((s) => [s.itHandle || s.handle, s])
    );

    const items = siblings
      .map((s) => {
        const itRef = itByHandle.get(s.itHandle) || itByHandle.get(s.handle);
        const titleForColor = itRef?.title || s.title;
        return {
          ...s,
          color: colorFromTitle(titleForColor, base),
        };
      })
      .filter((s) => s.color.length > 0)
      .sort((a, b) => a.color.localeCompare(b.color, 'it'));

    const activeItem = items.find((x) => x.handle === activeHandle) || items[0];
    const activeColor = activeItem?.color || '';

    mount.innerHTML = `
      <div class="vs-heading">${colorLabel}</div>
      <div class="vs-grid" role="list" aria-label="${colorLabel}">
        ${items
          .map((it) => {
            const img = (it.image || '').replace(/\"/g, '');
            const isCurrent = it.handle === activeHandle;
            return `
              <button
                type="button"
                class="vs-chip"
                data-sibling-handle="${it.handle}"
                data-product-url="${cleanProductUrl(it.url)}"
                aria-current="${isCurrent ? 'true' : 'false'}"
                aria-label="${it.color}"
              >
                <span class="vs-thumb"><img alt="" loading="lazy" src="${img}"></span>
                <span class="vs-chip-name">${it.color}</span>
              </button>
            `;
          })
          .join('')}
      </div>
    `;
  }

  function setPdpUrlFrozen(productInfo) {
    if (!productInfo) return;
    productInfo.dataset.updateUrl = 'false';
  }

  function swapToProduct(productInfo, productUrl, { keepOptions } = { keepOptions: true }) {
    if (!productInfo?.renderProductInfo || !productInfo?.handleSwapProduct) {
      return Promise.resolve(false);
    }

    const selections = keepOptions ? captureSelectedOptions(productInfo) : [];
    STATE.pendingSelections = selections;
    setPdpUrlFrozen(productInfo);
    setSwapLoading(productInfo, true);

    const cleanUrl = cleanProductUrl(productUrl);
    const applySwap = (html) => {
      STATE.pendingInitialSwap = false;
      releasePdpPending(productInfo);
      try {
        productInfo.handleSwapProduct(cleanUrl, false)(html);
      } finally {
        const pi = getProductInfo();
        setSwapLoading(pi, false);
        if (STATE.pendingSelections) {
          applySelectedOptions(pi, STATE.pendingSelections);
          STATE.pendingSelections = null;
        }
        finalizeSiblingUi(pi);
      }
    };
    const requestUrl = `${cleanUrl}?section_id=${encodeURIComponent(productInfo.dataset.section)}`;

    const prefetched = window.__VS_PREFETCH__;
    if (prefetched && typeof prefetched.then === 'function') {
      window.__VS_PREFETCH__ = null;
      return prefetched.then((responseText) => {
        if (responseText) {
          applySwap(parseSectionHtml(responseText));
          return true;
        }
        productInfo.renderProductInfo({
          requestUrl,
          targetId: productInfo.id,
          callback: applySwap,
        });
        return true;
      });
    }

    productInfo.renderProductInfo({
      requestUrl,
      targetId: productInfo.id,
      callback: applySwap,
    });
    return Promise.resolve(true);
  }

  function onProductInfoLoaded() {
    const pi = getProductInfo();
    if (!pi) return;

    // Ignore the first load of the master product while we prepare the sibling swap.
    if (STATE.pendingInitialSwap) return;

    setSwapLoading(pi, false);

    if (STATE.pendingSelections) {
      applySelectedOptions(pi, STATE.pendingSelections);
      STATE.pendingSelections = null;
    }

    finalizeSiblingUi(pi);
  }

  async function bootstrap() {
    const productInfo = getProductInfo();
    if (!productInfo) return;

    const currentHandle = getCurrentHandle(productInfo);
    const pathHandle = getPathHandle();
    const title = getCurrentTitle(productInfo);
    if (!currentHandle || !title) return;

    const rawSiblingParam = QS.get('sibling');
    const needsInitialSwap =
      !!window.__VS_PENDING_SWAP__ ||
      (!!rawSiblingParam &&
        !!QS.get('base') &&
        canonicalHandle(rawSiblingParam) !== canonicalHandle(currentHandle));

    if (needsInitialSwap) {
      markPdpPending(productInfo);
    }

    // Determine base + siblings (Italian catalog is the source of truth).
    let base = QS.get('base');
    let siblings = null;
    let itSiblings = null;
    let enSiblings = null;
    let masterHandle = null;

    if (base) {
      const group = await buildSiblingGroup(base);
      if (!group) {
        releasePdpPending(productInfo);
        return;
      }
      base = group.base;
      siblings = group.siblings;
      itSiblings = group.itSiblings;
      enSiblings = group.enSiblings;
      masterHandle = group.masterHandle;
    } else {
      const inferred = await inferGroupFromTitle(title, currentHandle, getProductImage(productInfo));
      if (!inferred) {
        releasePdpPending(productInfo);
        return;
      }
      base = inferred.base;
      siblings = inferred.siblings;
      itSiblings = inferred.itSiblings;
      enSiblings = inferred.enSiblings;
      masterHandle = inferred.masterHandle;
    }

    if (!siblings || siblings.length < 2 || !masterHandle) {
      releasePdpPending(productInfo);
      return;
    }

    const rawSibling = QS.get('sibling');
    const activeItHandle = resolveToItHandle(
      rawSibling || currentHandle,
      itSiblings,
      siblings
    );
    const activeHandle =
      resolveToLocaleHandle(activeItHandle, siblings) ||
      resolveSiblingHandle(rawSibling || currentHandle, siblings) ||
      currentHandle;

    exposePdpHelpers();

    STATE.base = base;
    STATE.siblings = siblings;
    STATE.itSiblings = itSiblings;
    STATE.enSiblings = enSiblings;
    STATE.masterHandle = masterHandle;
    STATE.activeItHandle = activeItHandle;
    STATE.activeHandle = activeHandle;

    if (pathHandle !== masterHandle) {
      window.location.replace(
        buildPdpUrl(masterHandle, base, activeHandle, getStorefrontRoot())
      );
      return;
    }

    // Freeze URL updates on master
    setPdpUrlFrozen(productInfo);

    const willSwap =
      activeHandle &&
      activeHandle !== masterHandle &&
      canonicalHandle(currentHandle) !== canonicalHandle(activeHandle);

    if (willSwap) {
      const sib = siblings.find((s) => s.handle === activeHandle);
      if (sib?.url) {
        await swapToProduct(productInfo, sib.url, { keepOptions: false });
        return;
      }
    }

    STATE.pendingInitialSwap = false;
    releasePdpPending(productInfo);
    finalizeSiblingUi(productInfo);
  }

  // Delegate click handler so it survives product-info swaps
  document.addEventListener(
    'click',
    (e) => {
      const btn = e.target.closest('[data-auto-color-siblings] button[data-product-url][data-sibling-handle]');
      if (!btn) return;
      e.preventDefault();

      const url = btn.getAttribute('data-product-url');
      const handle = btn.getAttribute('data-sibling-handle');
      if (!url || !handle) return;

      const pi = getProductInfo();
      if (!pi) return;

      STATE.activeHandle = handle;
      STATE.activeItHandle = resolveToItHandle(handle, STATE.itSiblings, STATE.siblings);

      swapToProduct(pi, url, { keepOptions: true });
    },
    true
  );

  // Keep behavior correct after swaps
  document.addEventListener('product-info:loaded', onProductInfoLoaded, true);

  // Run after product scripts are loaded (they're deferred), so DOMContentLoaded is fine.
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => bootstrap().catch(() => {}), { once: true });
  } else {
    bootstrap().catch(() => {});
  }
})();

